import styled from 'styled-components';
import { PageHeader, Table, Badge, Tag, Form, Input, Select, Popconfirm, Button, Tooltip, Modal, message } from "antd";
import { CopyOutlined, DeleteOutlined, FileProtectOutlined, HistoryOutlined, PlusOutlined, WarningTwoTone } from "@ant-design/icons";
import useQuery from "../../hooks/useQuery";
import usePaginatedRecordList from "../../hooks/usePaginatedRecordList";
import React, { useEffect, useRef, useState } from "react";
import { SyncOutlined, LinkOutlined } from "@ant-design/icons";
import { range } from "ramda";
import FormDrawer from "../../components/FormDrawer";
import RecordSelect from "../../components/fields/RecordSelect";
import { UnitModel, IUnit, InvoiceModel } from "../../types/models";
import moment from "moment/moment";
import { ancanaApi } from "../../api/ancanaApi";
import { IUser, UserModel } from "../../types/models";
import { ExpenseStatementModel } from "../../types/models/statements";
import CustomActionButton from "../../components/CustomActionButton";
import RecordTable from "../../components/RecordTable";
import ChangeLogDrawer from "../../components/ChangeLogDrawer";

const Container = styled.div`
  height: 100%;

  .ant-table-cell-ellipsis {
    p {
      text-overflow: ellipsis;
      overflow: hidden;
      margin-bottom: 0;
    }
  }
`;


// Each call issues a fresh single-use invite token (earlier links, including
// the emailed one, keep working until one of them is used or they expire), so
// the link can't be built here from the email alone any more.
const showInviteLinks = (r: any) => {
  ancanaApi.post(`unit-owners/${r.id}/invite-link/`).then(res => {
    const linkRow = (label: string, url: string) => (
      <>
        Link in {label}: <br />
        <div className="disp-flex h-stack-strict gap-xs justify-between">
          <Input value={url} readOnly />
          <Button
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(url);
              message.success(`Link in ${label} copied!`);
            }}
          />
        </div>
      </>
    );
    Modal.info({
      title: 'Owner Registration Link',
      width: 650,
      content: (
        <div className="pt-md pb-md">
          {linkRow('English', res.data.en)}
          <br />
          {linkRow('Spanish', res.data.es)}
          <br />
          <small>Single use, expires in 14 days.</small>
        </div>
      )
    });
  }).catch(err => {
    if (err.response?.status === 409) {
      message.info('This owner already set a password; they can log in.');
    } else {
      message.error('Could not create the registration link.');
    }
  });
};

// "View as owner". The link carries a single-use, 60-second code rather than
// the owner's permanent token (the portal's analytics pixels see the URL). The
// tab opens before the request so the popup blocker treats it as the click's.
const openOwnerPortal = (r: any) => {
  const tab = window.open('', '_blank');
  ancanaApi.post(`unit-owners/${r.id}/impersonate/`).then(res => {
    if (tab) tab.location.href = `${process.env.REACT_APP_ANCANA_SITE_URL}/owners?opv=${res.data.code}`;
  }).catch(() => {
    tab?.close();
    message.error('Could not open the owner portal.');
  });
};

const getColumns = (deleteRecord: any, patchRecord: any, setCurrentOwnerInvoices: any, setOwnerForChangeLog: any) => {
  const allColsSettings = { ellipsis: true };
  return [
    // { title: 'ID', dataIndex: 'id', sorter: true, key: 'id', width: 50 },
    { title: 'Owner', key: 'owner', sorter: true, render: (ho: any) => (
        <a className="h-stack-strict" href={`owners/${ho.id}`}>
          <div>{ho.owner.full_name}</div>
          <small>{ho.owner.email}</small>
        </a>
      ),
      width: 250,
    },
    {
      title: 'Unit', dataIndex: ['unit'], render: (unit: any) => (
      <>{unit.name}</>
      ), sorter: true, key: 'house',
      width: 150,
    },
    { title: 'Fractions', dataIndex: 'fraction_numbers', render: (frs: any) => <>{frs.map((f: number) => <Tag>#{f}</Tag>)}</>, key: 'fraction_numbers', width: 130 },
    {
      title: 'SPEI Account',
      dataIndex: ['owner', 'stripe_accounts', 0],
      render: (acc: any) => (
        <div className="disp-flex h-stack-strict justify-between">
          <Tooltip
            title={(
              <div>
                <small>{acc?.stripe_id && acc?.livemode ? 'Live Mode' : 'Test Mode'}</small>
                <div style={{ fontFamily: "monospace" }}>{acc?.stripe_id}</div>
              </div>
            )}
          >
            <div className="h-stack-strict">
              <Badge status={acc?.stripe_id && acc?.livemode ? "success" : "default"} />
              <span style={{ fontSize: 11 }}>{acc?.speiaccount.bank_name}</span>
              <p style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 500 }}>{acc?.speiaccount.clabe}</p></div>
          </Tooltip>
        </div>
      ),
      key: 'spei',
      width: 165,
    },
    {
      title: 'Cash Balance',
      dataIndex: ['owner', 'stripe_accounts', 0],
      render: (acc: any) => (
        <div className="h-stack-strict" style={{ display: 'flex', flexWrap: 'wrap', gap: 0, placeSelf: 'flex-end' }}>
          <Tooltip title={`${acc?.cb_reconciliation_mode} reconciliation`}>
            <div className="mr-md">${(acc?.mx_cash_balance|0/100).toLocaleString()} MXN</div>
          </Tooltip>
        </div>
      ),
      key: 'mx_cash_balance',
      width: 165,
      align: "center"
    },
    /*
    {
      title: 'Expenses', dataIndex: ['main_accounting_journal'],
      render: (journal: any | null) => <><Badge status={journal ? 'success':'error'} text={journal ? 'ready' : 'pending setup'} /></>, key: 'journal', width: 100 },
     */
    { title: 'Created at', dataIndex: 'created_at', sorter: true, render: (c: string) => moment(c).format('ll'), width: 120 },
    {
      title: 'Actions', render: (r: any) => (
        <div className="row-actions">
          <a onClick={() => setCurrentOwnerInvoices(r)}>
            <FileProtectOutlined /> Invoices
          </a>

          {r.can_impersonate ? (
            <a onClick={() => openOwnerPortal(r)}>
              <LinkOutlined /> Portal
            </a>) : (
            <a onClick={() => showInviteLinks(r)}>
              <CopyOutlined /> Get Link
            </a>
          )}
          <Popconfirm
            title={`Permanently delete this owner?`}
            onConfirm={() =>deleteRecord(r.id)}
            okText="Yes"
          >
            <a><DeleteOutlined /> Delete</a>
          </Popconfirm>
          <Tooltip title="View change log">
            <a onClick={() => setOwnerForChangeLog(r)}><HistoryOutlined /></a>
          </Tooltip>
        </div>
      ),
      width: 248
    }
  ].map((e: any) => ({ ...e, ...allColsSettings }));
}

function Owners() {
  const { query, handleSearch } = useQuery();
  const {
    pagination,
    createRecord,
    deleteRecord,
    patchRecord,
    handleChange,
    isLoading,
    doRecordAction,
    recordList: owners
  } = usePaginatedRecordList<any>({
    query,
    recordPath: 'unit-owners',
    extraQueryAppend: '&expand=owner.stripe_accounts.speiaccount,unit,can_impersonate&omit=owner.groups',
    apiService: 'admin',
    pageSize: '25',
    initialAutoFetch: true
  });
  const elementRef: any = useRef(null);
  const [selectedHouseId, setSelectedHouseId] = useState();
  const [selectedHouseFractions, setSelectedHouseFractions] = useState<{ soldFractions: number[], totalFractions: number } | undefined>();
  const [currentOwnerInvoices, setCurrentOwnerInvoices] = useState<any>({ id: 0 });
  const [ownerForChangeLog, setOwnerForChangeLog] = useState<any>(null);

  useEffect(() => {
    if (selectedHouseId) {
      ancanaApi.get(`housing-units/${selectedHouseId}/?expand=unit_ownerships`).then(res => {
        const totalFractions = res.data.fractions;
        const soldFractions = res.data.unit_ownerships.reduce((acc: any, curr:any) => acc+curr.fraction_numbers, []);
        setSelectedHouseFractions({ totalFractions, soldFractions });
      })
    }
  }, [selectedHouseId]);

  return (
    <Container ref={elementRef}>
      <PageHeader
        title="Owners"
        subTitle={`${pagination.total?.toLocaleString('en-US')} records`}
        extra={[
          <Button onClick={() => doRecordAction('refresh-cash-balances')}><SyncOutlined /> Cash Balances</Button>,
          <FormDrawer
            recordName="Owner"
            handleRecordAction={(data: any) => doRecordAction('create-new', 'post', data)}
            handleValuesChange={(changedValues: any, allValues: any) => {
              setSelectedHouseId(allValues['unit'] || undefined);
            }}
            inputs={[
              { name: "email", type: "email" },
              { name: "first_name" },
              { name: "last_name" },
            ]}
            btnIcon={<PlusOutlined />}
          >
            {/*
            <RecordSelect<IUser>
              recordModel={UserModel}
              formName="owner"
              allowManage
            />*/}
            <RecordSelect<IUnit>
              recordModel={UnitModel}
              extraFilterQuery="&published=true"
              allowManage
            />
            <Form.Item
              name="fraction_numbers"
              label="Fractions"
              rules={[{ required: true }]}
              // initialValue={1}
            >
              <Select mode="multiple" disabled={!selectedHouseFractions}>
                {!!selectedHouseFractions && (
                  range(1,selectedHouseFractions.totalFractions+1).map(fn => (
                    <Select.Option
                      disabled={selectedHouseFractions.soldFractions.includes(fn)}
                      value={fn}
                    >
                      #{fn} {selectedHouseFractions.soldFractions.includes(fn) ? '(sold)' : ''}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </FormDrawer>
        ]}
        className="mb-md"
      >
        <Input.Search
          placeholder="Search by house, owner"
          onSearch={handleSearch}
          defaultValue={query.get('search') || ''}
          allowClear
          style={{ width: 300 }}
        />
        <RecordSelect<IUnit>
          recordModel={UnitModel}
          extraFilterQuery="&
          =false"
          filter
        />
      </PageHeader>

      <Table
        columns={getColumns(deleteRecord, patchRecord, setCurrentOwnerInvoices, setOwnerForChangeLog)}
        dataSource={owners}
        pagination={pagination}
        loading={isLoading}
        onChange={handleChange}
        scroll={{ y: elementRef.current?.clientHeight - 180 }}
        size="small"
      />

      {ownerForChangeLog && (
        <ChangeLogDrawer
          recordPath="unit-owners"
          recordId={ownerForChangeLog.id}
          recordName={`${ownerForChangeLog.owner?.full_name || ''} — ${ownerForChangeLog.unit?.name || ''}`}
          open={!!ownerForChangeLog}
          onClose={() => setOwnerForChangeLog(null)}
        />
      )}

      <Modal
        visible={currentOwnerInvoices.id > 0} onCancel={() => setCurrentOwnerInvoices({ id: 0 })}
        width="90%"
        footer={null}
        title={currentOwnerInvoices.id > 0 ? `${currentOwnerInvoices?.owner?.full_name || ''}` : ''}
      >
        <RecordTable
          recordModel={InvoiceModel}
          recordsetFilters={{ owner: currentOwnerInvoices.id }}
          btnType="link"
          btnSize="small"
          btnText="Invoices"
          embedded
          actionsWhitelist={[]}
        />
      </Modal>
    </Container>
  )
}

export default Owners;
